// CartContext.jsx — VERSION VERROUILLÉE / PRO (FIX FINAL + CLOUD READY)

import { createContext, useContext, useEffect, useState } from "react";
import api from "./axiosConfig";

const CartContext = createContext();
const STORAGE_KEY = "souqi_cart_v2";

const DEBUG = true;
const warn = (...args) => DEBUG && console.warn("[Cart]", ...args);

function safePrice(value) {
  const num = Number(
    typeof value === "string" ? value.replace(/[^\d.-]+/g, "") : value
  );
  return Number.isFinite(num) && num >= 0 ? num : 0;
}

/**
 * ✅ NORMALISATION INTELLIGENTE DE L'IMAGE
 * Gère Cloudinary (http...) et Local (/uploads...)
 */
function normalizeImage(raw) {
  if (!raw) return "";

  // Cas où raw est directement le chemin (string)
  if (typeof raw === "string") {
    if (raw.startsWith("http")) return raw; // Déjà une URL (Cloudinary)
    if (raw.startsWith("/uploads")) return `https://souqi-api.onrender.com${raw}`;
    return raw;
  }

  // Cas où raw est un objet produit complet
  const direct =
    raw.image ||
    raw.imageUrl ||
    raw.photo ||
    raw.thumbnail ||
    raw.picture ||
    "";

  if (typeof direct === "string" && direct) {
    if (direct.startsWith("http")) return direct;
    if (direct.startsWith("/uploads")) return `https://souqi-api.onrender.com${direct}`;
    return direct;
  }

  // Cas des tableaux d'images
  const fromArray =
    Array.isArray(raw.images) && raw.images.length > 0 ? raw.images[0] : "";

  if (typeof fromArray === "string" && fromArray) {
    if (fromArray.startsWith("http")) return fromArray;
    if (fromArray.startsWith("/uploads")) return `https://souqi-api.onrender.com${fromArray}`;
    return fromArray;
  }

  return "";
}

/**
 * ✅ NORMALISATION ROBUSTE PRODUIT
 */
function normalizeProduct(raw, forcedMerchantId = null) {
  if (!raw) return null;

  const id = raw._id || raw.id || raw.productId;
  const name = raw.name || raw.nom;
  const price = safePrice(raw.price ?? raw.prix);
  const quantity = Math.max(1, Number(raw.quantity) || 1);

  let merchantId = forcedMerchantId;

  if (!merchantId) {
    if (typeof raw.merchantId === "string") {
      merchantId = raw.merchantId;
    } else if (raw.merchantId?._id) {
      merchantId = raw.merchantId._id;
    } else if (raw.merchant?._id) {
      merchantId = raw.merchant._id;
    }
  }

  const merchantName =
    raw.merchantName ||
    raw.merchant?.name ||
    raw.merchantId?.name ||
    null;

  const image = normalizeImage(raw); // Utilise la nouvelle logique intelligente

  if (!id || !name || !merchantId) {
    warn("Produit rejeté (id / name / merchantId manquant):", raw);
    return null;
  }

  return {
    id,
    name,
    price,
    quantity,
    image,
    merchantId: String(merchantId), 
    merchantName,
  };
}

export function CartProvider({ children }) {
  const [cart, setCart] = useState([]);
  const [merchantName, setMerchantName] = useState(null);
  const [promoActive, setPromoActive] = useState(false); // ✅ Ajouté pour la promo

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (!parsed) return;
      setCart(parsed.cart || []);
      setMerchantName(parsed.merchantName || null);
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  // ✅ Logique de détection de promo intégrée au context
  useEffect(() => {
    async function checkMerchantPromo() {
      if (cart.length > 0 && cart[0].merchantId) {
        try {
          const res = await api.get(`/merchants/public/${cart[0].merchantId}`);
          setPromoActive(res.data?.merchant?.freeServiceOrdersLeft > 0);
        } catch (e) {
          setPromoActive(false);
        }
      } else {
        setPromoActive(false);
      }
    }
    checkMerchantPromo();

    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ cart, merchantName })
    );
  }, [cart, merchantName]);

  const addToCart = (rawProduct) => {
    const product = normalizeProduct(rawProduct);
    if (!product) return;

    setCart((prev) => {
      const prevMerchantId = prev[0]?.merchantId;

      if (
        prev.length > 0 &&
        prevMerchantId &&
        String(prevMerchantId) !== String(product.merchantId)
      ) {
        const ok = window.confirm(
          "Votre panier contient déjà des articles d’un autre supermarché.\n\n" +
            "Voulez-vous vider le panier et continuer avec ce supermarché ?"
        );

        if (!ok) return prev;
        setMerchantName(product.merchantName || "Supermarché");
        return [{ ...product, quantity: 1 }];
      }

      const existing = prev.find((p) => p.id === product.id);
      if (existing) {
        return prev.map((p) =>
          p.id === product.id ? { ...p, quantity: p.quantity + 1 } : p
        );
      }

      if (prev.length === 0) {
        setMerchantName(product.merchantName || "Supermarché");
      }

      return [...prev, product];
    });
  };

  const increaseQty = (id) =>
    setCart((prev) =>
      prev.map((p) => (p.id === id ? { ...p, quantity: p.quantity + 1 } : p))
    );

  const decreaseQty = (id) =>
    setCart((prev) =>
      prev
        .map((p) => (p.id === id ? { ...p, quantity: p.quantity - 1 } : p))
        .filter((p) => p.quantity > 0)
    );

  const removeFromCart = (id) => {
    setCart((prev) => {
      const next = prev.filter((p) => p.id !== id);
      if (next.length === 0) setMerchantName(null);
      return next;
    });
  };

  const clearCart = () => {
    setCart([]);
    setMerchantName(null);
    localStorage.removeItem(STORAGE_KEY);
  };

  const setCartFromOrder = async (items, merchantId) => {
    try {
      if (!Array.isArray(items) || items.length === 0) return;
      const mid = typeof merchantId === "string" ? merchantId : merchantId?._id ? String(merchantId._id) : String(merchantId || "");
      if (!mid) return;

      let resolvedMerchantName = "Supermarché";
      try {
        const mRes = await api.get(`/merchants/public/${mid}`);
        resolvedMerchantName = mRes.data?.merchant?.name || mRes.data?.merchant?.storeName || resolvedMerchantName;
      } catch (e) {}

      const ids = Array.from(new Set(items.map((it) => String(it.productId || it._id || "")).filter(Boolean)));
      let products = [];
      if (ids.length) {
        const pRes = await api.post("/products/by-ids", { ids });
        products = pRes.data?.products || [];
      }

      const rebuilt = items.map((ordered) => {
        const pid = String(ordered.productId || ordered._id || "");
        const p = products.find((x) => String(x._id) === pid);
        if (p) {
          return normalizeProduct({ ...p, quantity: ordered.quantity || 1, merchantName: p.merchantName || resolvedMerchantName }, mid);
        }
        return normalizeProduct({
          _id: pid || `fallback-${Date.now()}`,
          name: ordered.name,
          price: ordered.price,
          quantity: ordered.quantity || 1,
          image: ordered.image || "",
          merchantId: mid,
          merchantName: resolvedMerchantName,
        }, mid);
      }).filter(Boolean);

      setCart(rebuilt);
      setMerchantName(resolvedMerchantName);
    } catch (e) {
      console.error("❌ setCartFromOrder error:", e);
    }
  };

  const total = cart.reduce((sum, p) => sum + p.price * p.quantity, 0);
  const itemCount = cart.reduce((s, p) => s + p.quantity, 0);

  return (
    <CartContext.Provider value={{ 
      cart, 
      merchantName, 
      addToCart, 
      increaseQty, 
      decreaseQty, 
      removeFromCart, 
      clearCart, 
      setCartFromOrder, 
      total, 
      itemCount,
      promoActive // ✅ Exporté ici
    }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
